/**
 * Dev/testing affordances, gated to non-production environments so they never reach real users.
 *
 * "Dev" = the local dev server, a LAN dev server (for on-device testing), or the staging host —
 * but NEVER production (abcshooter.web.app / the iOS app's prod URL). Both staging and prod ship
 * the same production build, so the only runtime signal is the hostname.
 *
 * Each affordance is opt-in via a URL flag, e.g. `?survival` exposes the Survival UFO on the menu
 * without having to earn it first (3 stars, or Mars in a single game).
 *
 * The per-mode flags show ONE treat, because they exist for playing that mode. **`?treats` is the
 * exception**: it fills every bank to its cap at once, so the menu can be looked at when it is as
 * full as it can ever be — a layout question that is otherwise unanswerable, since seeing it for
 * real means earning six different treats to their caps in a single profile. Nothing is spendable
 * under it and nothing is claimed.
 */
export function isDevEnv(): boolean {
  // No `location` outside the browser (vitest/node) — never a dev affordance there.
  if (typeof location === 'undefined') return false;
  const h = location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    // Private LAN ranges (phone hitting the dev server over Wi-Fi).
    /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(h) ||
    // Firebase staging hosting (abcshooter-staging.web.app / .firebaseapp.com).
    h.includes('staging')
  );
}

/**
 * Which build the player is actually looking at — `"staging · v0.1.41 · 0816-1639"` — or null on
 * production, where it must never be rendered.
 *
 * Exists because the native shell's version and the running web build are decoupled. Android serves
 * every launch from the service-worker precache and applies a new deploy only on the *next* visit to
 * the Menu (specs/auto-update.md D2/D9), so an app reporting "0.1.41-staging" in Android Settings can
 * legitimately be running the web build from a deploy or two ago — which once read as "staging is
 * pointing at production". The build time, not just the version, is what settles it: a redeploy
 * without a version bump still moves the stamp.
 *
 * Gated by the same isDevEnv() as every other affordance here — one hostname check, so production
 * can't start showing developer text through a second, drifting copy of the rule.
 */
export function buildStamp(): string | null {
  if (!isDevEnv()) return null;
  const h = location.hostname;
  // A LAN address shows the bare IP: several phones can be pointed at different dev servers at once,
  // and "which machine is this?" is then the useful half of the answer.
  const env = h === 'localhost' || h === '127.0.0.1' ? 'local' : h.includes('staging') ? 'staging' : h;
  return `${env} · v${__APP_VERSION__} · ${__BUILD_TIME__}`;
}

/** True when a dev URL flag (e.g. `?survival`) is present AND we're in a non-production env. */
export function devFlag(name: string): boolean {
  return isDevEnv() && new URLSearchParams(location.search).has(name);
}

/**
 * True when a URL flag is present on ANY host, production included.
 *
 * Deliberately rare: only for a flag that is meant to reach real visitors on the live site.
 * Today that is `?demo` alone (src/game/clip.ts) — a capped trial is something we *want* to be
 * able to link people to, and App Review needs a production URL that actually enters it.
 * Everything else stays on devFlag(), which is inert on production.
 */
export function urlFlag(name: string): boolean {
  if (typeof location === 'undefined') return false;
  return new URLSearchParams(location.search).has(name);
}

/** Value of a dev URL param (e.g. `?board=pirates` → 'pirates'), or null outside dev envs. */
export function devParam(name: string): string | null {
  return isDevEnv() ? new URLSearchParams(location.search).get(name) : null;
}

/**
 * Value of a URL param on ANY host, production included — what urlFlag is to devFlag.
 *
 * Deliberately rare for the same reason as urlFlag: only for a param meant to reach real visitors
 * on the live site. Today that is `?lesson=` alone (src/game/packs.ts), so an ad can land on the
 * lesson it advertises instead of the language default.
 */
export function urlParam(name: string): string | null {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
